import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * PDF viewer (86d3udn5u follow-up) — embeds the BROWSER'S OWN PDF viewer.
 *
 * Deliberately not a JS PDF renderer. Every current browser ships a full viewer (continuous scroll,
 * zoom, page thumbnails, text selection, Ctrl+F, rotate, print, save) that is faster, more
 * accessible and more familiar than anything we would hand-roll — and it costs zero bytes and zero
 * rendering code. An earlier react-pdf/pdf.js implementation was reverted: driving canvas
 * rasterisation from a measured container width is a feedback cycle (width → page size → content
 * height → scrollbar toggles → measured width changes → re-rasterise → repeat) which showed up as a
 * looping flicker, and it shipped ~400 kB gzipped of worker onto the customer-facing page.
 *
 * `url` must render INLINE, so pass an object URL built from a `application/pdf` Blob. A plain
 * endpoint URL is wrong for two reasons: the internal `/file` route needs a bearer token an iframe
 * cannot send, and the public route answers `Content-Disposition: attachment`, which makes a frame
 * download instead of display. Both callers therefore fetch bytes first (each with its own client —
 * the public page must never touch `apiClient`) and hand the object URL down.
 */
interface PdfDocumentViewerProps {
  /** Object URL for an application/pdf Blob. `null` while the bytes are still loading. */
  url: string | null;
  /** Optional label used by assistive tech (e.g. the document number). */
  title?: string;
  /**
   * Layout override. Defaults to a fixed reading height; pass `h-auto flex-1 min-h-0` to let it fill
   * a flex parent instead (tailwind-merge lets the caller's height win).
   */
  className?: string;
}

export function PdfDocumentViewer({ url, title = 'Document', className }: PdfDocumentViewerProps) {
  return (
    <div
      className={cn(
        'flex h-[78vh] flex-col overflow-hidden rounded-xl border border-border bg-muted/30',
        className
      )}
    >
      {url ? (
        <>
          {/* #view=FitH asks the native viewer to fit the page width; ignored where unsupported. */}
          <iframe
            src={`${url}#view=FitH`}
            title={`${title} — PDF`}
            className="w-full flex-1 border-0 bg-card"
          />
          {/* Some in-app browsers (notably older iOS webviews) refuse to embed PDFs and render an
              empty frame. This always-present escape hatch means those users are never stuck. */}
          <div className="shrink-0 border-t border-border px-3 py-2">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              Not showing? Open the PDF in a new tab
            </a>
          </div>
        </>
      ) : (
        <div className="flex-1 p-3">
          <Skeleton className="h-full w-full rounded" />
          <span className="sr-only">Loading document…</span>
        </div>
      )}
    </div>
  );
}
